import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import TableReservationWidget from '@/components/restaurant/TableReservationWidget';
import GuestChat from '@/components/chat/GuestChat';
import { Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/config/api';

import RestaurantHeader from '../components/RestaurantHeader';
import CategoryNav from '../components/CategoryNav';
import CategorySection from '../components/CategorySection';
import MenuSkeleton from '../components/MenuSkeleton';
import ItemDetailModal from '../components/ItemDetailModal';
import FloatingCartButton from '../components/FloatingCartButton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCart } from '../context/CartContext';

export default function StorefrontPage() {
  const { restaurantSlug } = useParams();
  const { setRestaurantSlug } = useCart();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  
  const categoryRefs = useRef({});


  const fetchMenu = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/storefront/menu/${restaurantSlug}`);
      setRestaurant(response.data.restaurant);
      setCategories(response.data.categories);
      
      if (response.data.categories.length > 0) {
        setActiveCategory(response.data.categories[0].id);
      }
    } catch (err) {
      console.error('Failed to load menu:', err);
      setError(
        err.response?.data?.detail || 
        'Unable to load menu. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  }, [restaurantSlug]);

  useEffect(() => {
    if (restaurantSlug) {
      fetchMenu();
      setRestaurantSlug(restaurantSlug);
    }
  }, [restaurantSlug, fetchMenu, setRestaurantSlug]);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    
    // Scroll to category
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      const yOffset = -200; // Offset for sticky header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setItemModalOpen(true);
  };

  const handleCloseModal = () => {
    setItemModalOpen(false);
    // Delay clearing selectedItem to allow modal exit animation
    setTimeout(() => setSelectedItem(null), 200);
  };

  // Update active category on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 250;
      
      for (const category of categories) {
        const element = document.getElementById(`category-${category.id}`);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveCategory(category.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories]);

  if (loading) {
    return <MenuSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Restaurant not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Restaurant Header */}
      <RestaurantHeader restaurant={restaurant} />

      {/* Table reservation (online.chain29.com) */}
      <div className="max-w-6xl mx-auto px-4 pt-4 flex justify-end">
        <TableReservationWidget restaurantId={restaurant.id} restaurantName={restaurant.name} />
      </div>
      
      {/* Category Navigation */}
      {categories.length > 0 && (
        <CategoryNav 
          categories={categories}
          activeCategory={activeCategory}
          onCategoryClick={handleCategoryClick}
        />
      )}
      
      {/* Menu Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {categories.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No menu items available at this time.
            </AlertDescription>
          </Alert>
        ) : (
          <div>
            {categories.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
                onItemClick={handleItemClick}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer Spacer */}
      <div className="h-20"></div>

      {/* Item Detail Modal */}
      <ItemDetailModal
        item={selectedItem}
        open={itemModalOpen}
        onClose={handleCloseModal}
      />

      {/* Floating Cart Button */}
      <FloatingCartButton />

      {/* Live chat with the restaurant's staff (left: the cart button is on the right) */}
      <GuestChat restaurantId={restaurant.id} restaurantName={restaurant.name} sourcePage="storefront" side="left" />
    </div>
  );
}
